import React from "react";
import OdooServices from "./OdooServices";
import HeroSection from "../common/HeroSection";
import { Wrench, Clock, ShieldCheck, RefreshCcw, CircleCheckBig } from "lucide-react";

export default function OdooSupportMaintenance() {
  return (
    <div>
      <HeroSection
        title="Odoo Support & Maintenance"
        breadcrumb={[
          { name: "Services", url: "/services" },
          { name: "Odoo Services", url: "/services/odoo-services" },
          {
            name: "Odoo Support & Maintenance",
            url: "/services/odoo-services/odoo-support-maintenance",
          },
        ]}
        background="/assets/images/about/bg.png"
      />

      <div className="bg-white">
        <div className="py-16 px-4 mx-auto container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 px-4">
            {/* Sidebar */}
            <OdooServices />

            {/* Content */}
            <div className="lg:col-span-2">
              <div className="relative rounded-xl overflow-hidden mb-8">
                <img
                  alt="Odoo Support and Maintenance"
                  src="/assets/images/odoo/odoo-support.webp"
                  className="w-full h-auto object-cover rounded-xl"
                  loading="lazy"
                />
              </div>

              <div className="mb-8">
                <span className="text-primary font-medium text-lg inline-block py-1 font-bold rounded-full bg-primary bg-opacity-10 mb-3">
                  Reliable & Ongoing Assistance
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  Odoo <span className="text-primary">Support</span> & Maintenance
                </h2>
                <div className="prose prose-lg max-w-none text-gray-600">
                  <p>
                    Our dedicated support team ensures your Odoo system runs smoothly with proactive maintenance, timely updates, and rapid issue resolution. Whether it's bug fixes, performance tuning, or functional support — we've got you covered.
                  </p>
                  <p>
                    At <span className="text-primary font-medium">STISSA Technology LLP (TISSA Technology)</span>, we help you get the most out of your Odoo investment by keeping your system reliable, optimized, and aligned with your business growth.
                  </p>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">What We Offer</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full mb-4 inline-flex items-center justify-center">
                      <Wrench className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      Bug Fixing & Troubleshooting
                    </h4>
                    <p className="text-gray-600">
                      Quickly resolve system issues and bugs to ensure smooth Odoo operation with minimal disruption.
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full mb-4 inline-flex items-center justify-center">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      24/7 Monitoring & Support
                    </h4>
                    <p className="text-gray-600">
                      Continuous monitoring of your Odoo system with real-time alerts and round-the-clock support.
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full mb-4 inline-flex items-center justify-center">
                      <ShieldCheck className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      Security & Compliance
                    </h4>
                    <p className="text-gray-600">
                      Keep your Odoo environment secure with patch management, access control, and GDPR-compliant practices.
                    </p>
                  </div>

                  <div className="bg-white p-6 rounded-xl shadow-soft border border-gray-100 hover:shadow-md transition-shadow duration-300">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-full mb-4 inline-flex items-center justify-center">
                      <RefreshCcw className="h-6 w-6 text-primary" />
                    </div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">
                      System Upgrades & Optimization
                    </h4>
                    <p className="text-gray-600">
                      Upgrade to the latest Odoo versions with optimized configurations tailored to your workflows.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  Benefits of Our Support Services
                </h3>
                <div className="bg-gray-50 p-6 rounded-xl border-l-4 border-primary">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      "Minimized system downtime with proactive monitoring",
                      "Fast issue resolution by expert support engineers",
                      "Continuous improvements and system tuning",
                      "Secure and compliant Odoo environment",
                      "Cost-effective maintenance plans",
                      "Priority access to new features and updates",
                    ].map((benefit, index) => (
                      <div className="flex items-start" key={index}>
                        <CircleCheckBig className="h-5 w-5 text-primary mr-3 mt-1" />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl border border-blue-100">
                  <h4 className="text-2xl font-bold text-gray-800 mb-3">
                    Need Reliable Odoo Support?
                  </h4>
                  <p className="text-gray-600 mb-6">
                    Let our experts handle your Odoo system's maintenance so you can focus on what matters — growing your business.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      className="inline-block text-blue-700 font-bold bg-primary hover:bg-primary-dark py-3 px-6 rounded-lg transition-colors duration-300"
                      href="/contact-us/"
                    >
                       Request a Free Consultation
                    </a>
                    <a
                      className="inline-block border border-primary text-primary hover:bg-blue-700 hover:text-white py-3 px-6 rounded-lg transition-colors duration-300"
                      href="/services/odoo-services/"
                    >
                      Explore More Odoo Services
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* End Content */}
          </div>
        </div>
      </div>
      <section className="py-16 bg-gray-50">
  <div className="container mx-auto">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
        Frequently Asked Questions
      </h2>
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-soft">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            What does Odoo support & maintenance include?
          </h3>
          <p className="text-gray-600">
            Our Odoo support and maintenance service covers issue resolution, system performance optimization, version upgrades, security patches, and user support. We help ensure your system runs smoothly at all times.
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-soft">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Do you provide emergency support?
          </h3>
          <p className="text-gray-600">
            Yes, we offer emergency support for critical issues affecting your business operations. Our team is available to assist based on your support plan and SLAs.
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-soft">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            How often do you perform system health checks?
          </h3>
          <p className="text-gray-600">
            We conduct regular health checks depending on your selected maintenance package—typically monthly or quarterly—to identify performance bottlenecks, outdated modules, and potential security vulnerabilities.
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-soft">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Will I get support for custom modules?
          </h3>
          <p className="text-gray-600">
            Yes, we provide maintenance and bug-fixing support for both standard and custom-developed Odoo modules, ensuring all components of your system remain functional.
          </p>
        </div>
        <div className="bg-white p-6 rounded-xl shadow-soft">
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Can I upgrade to the latest Odoo version with your support?
          </h3>
          <p className="text-gray-600">
            Absolutely. We help you migrate your existing Odoo instance to the latest version, preserving your data, customizations, and workflows with minimal downtime.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

    </div>
  );
}
